import {
	Fragment,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { AppLayout } from "../../../../containers/layout/app";
import { BookLoanWrapper } from "./styled";
import { AppContext } from "../../../../context/appContext";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { LoanSummaryTable } from "../../../../containers/tables/loansummarytable";
import { useQuery } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { retrieveLoanService } from "../../../../utils/api/loanmanagement/loansetup/retrieveLoan";
import { useNavigate, useParams } from "react-router-dom";
import { retrieveCustomerService } from "../../../../utils/api/customerservice/customerlist/retrieveCustomer";
import { formatAmountDisplay } from "../../../../helpers/formatAmountDisplay";
import { BaseButton } from "../../../../components/button/styled";
import AddIcon from "@mui/icons-material/Add";
import { CollateralTable } from "../../../../containers/tables/collateraltable";
import { retrieveAllCollateralService } from "../../../../utils/api/loanmanagement/collateral/retrieveAllCollateral";
import { BaseConfirmationModal } from "../../../../components/modal/confirmation";
import { deleteCollateralService } from "../../../../utils/api/loanmanagement/collateral/deleteCollateral";
import { AddCollateral } from "../../../../containers/form/addcollateral";
import {
	AddCollateralFormFieldsType,
	AddGuarantorFormFieldsType,
	LoanType,
} from "../../../../types/container.type";
import { addCollateralService } from "../../../../utils/api/loanmanagement/collateral/addCollateral";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { AlertModalCheckIcon } from "../../../../assets";
import { EditCollateral } from "../../../../containers/form/editcollateral";
import { editCollateralService } from "../../../../utils/api/loanmanagement/collateral/editCollateral";
import { GuarantorTable } from "../../../../containers/tables/guarantortable";
import { retrieveAllGuarantorService } from "../../../../utils/api/loanmanagement/guarantor/retrieveAllGuarantor";
import { AddGuarantor } from "../../../../containers/form/addguarantor";
import { addGuarantorService } from "../../../../utils/api/loanmanagement/guarantor/addGuarantor";
import { EditGuarantor } from "../../../../containers/form/editguarantor";
import { editGuarantorService } from "../../../../utils/api/loanmanagement/guarantor/editGuarantor";
import { deleteGuarantorService } from "../../../../utils/api/loanmanagement/guarantor/deleteGuarantor";
import { LoanChargesTable } from "../../../../containers/tables/loanchargestable";
import { retrieveAllSystemChargeService } from "../../../../utils/api/configuration/systemcharge/retrieveAllSystemCharge";
import { updateLoanApplicationService } from "../../../../utils/api/loanmanagement/loansetup/updateLoanApplication";
import { LoanRepaymentSchedule } from "../../../../containers/tables/loanrepaymentschedule";
import { retrieveLoanProductService } from "../../../../utils/api/configuration/loanproduct/retrieveLoanProduct";
import { LoanRepaymentDetailsTable } from "../../../../containers/tables/loanrepaymentdetailstable";
import { BookLoanForm } from "../../../../containers/form/bookloan";
import { bookLoanService } from "../../../../utils/api/loanmanagement/loansetup/bookLoan";
import { retrieveLoanRepaymentService } from "../../../../utils/api/loanmanagement/loanrepayment/retrieveLoanRepayment";

export const BookLoan = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const steps = [
		"Loan Summary",
		"Collateral",
		"Guarantor",
		"Charges",
		"Repayment Schedule",
		"Repayment Details",
		"Approval/Edit",
	];
	const callToActions = [
		"add collateral",
		"edit collateral",
		"add guarantor",
		"edit guarantor",
	] as const;

	type CallToAction = (typeof callToActions)[number];
	const initialCollateral: AddCollateralFormFieldsType = {
		name: "",
		type: " ",
		serialNo: "",
		estimatedPrice: "",
		attachments: [],
		description: "",
	};
	const initialGuarantor: AddGuarantorFormFieldsType = {
		firstName: "",
		middleName: "",
		lastName: "",
		email: "",
		primaryPhone: "",
		secondaryPhone: "",
		homeAddress: "",
		officeAddress: "",
		position: "",
	};

	const navigate = useNavigate();
	const { id = "" } = useParams();
	const { setActiveNavItem } = useContext(AppContext);
	const guarantorTableDropdownRef = useRef<HTMLUListElement | null>(null);
	const collateralTableDropdownRef = useRef<HTMLUListElement | null>(null);

	const [isLoading, setIsLoading] = useState(false);
	const [isBookingLoading, setIsBookingLoading] = useState(false);
	const [activeTabIndex, setActiveTabIndex] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [bookingError, setBookingError] = useState<string | null>(null);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
	const [selectedCallToAction, setSelectedCallToAction] =
		useState<CallToAction | null>(null);
	const [selectedGuarantor, setSelectedGuarantor] = useState<Record<
		string,
		any
	> | null>(null);
	const [selectedCollateral, setSelectedCollateral] = useState<Record<
		string,
		any
	> | null>(null);
	const [collateral, setCollateral] = useState(initialCollateral);
	const [guarantor, setGuarantor] = useState(initialGuarantor);
	const [loan, setLoan] = useState<LoanType | null>(null);
	const [repaymentSchedule, setRepaymentSchedule] = useState<
		Record<any, any>[] | null
	>(null);
	const [selectedLoanProduct, setSelectedLoanProduct] = useState<Record<
		string,
		any
	> | null>(null);

	useEffect(() => {
		setActiveNavItem("Loan Management");
	}, [setActiveNavItem]);

	useEffect(() => {
		if (!id) return;
		retrieveLoanService(TOKEN, id)
			.then(async (data) => {
				const attachments = await convertUrlToFile(data?.attachments);
				setLoan({
					serialNo: data?.serialNo,
					institution: data?.institution,
					obligor: data?.obligor,
					disbursementAccount: data?.disbursementAccount,
					loanProduct: data?.loanProductId,
					loanId: data?.serialNo,
					term: data?.term,
					interestRate: data?.interestRate,
					disbursementDate: new Date(data?.disbursementDate)
						.toISOString()
						.split("T")[0],
					firstRepaymentDate: new Date(data?.firstRepaymentDate)
						.toISOString()
						.split("T")[0],
					currency: data?.currency,
					appliedAmount: data?.appliedAmount,
					penalty: data?.penalty,
					attachments: attachments || [],
					description: data?.description,
					charges: data?.charges || [],
					status: data?.status,
				});
			})
			.catch((err) => {
				console.error("Failed to fetch loan application:", err);
			});
	}, [TOKEN, id]);

	useEffect(() => {
		if (!loan) return;
		const generateRepaymentSchedule = async () => {
			const loanProduct = await retrieveLoanProductService(
				TOKEN,
				loan?.loanProduct,
			);
			const repaymentMonthsCount =
				loanProduct?.term === "annually"
					? Number(loan?.term) * 12
					: Number(loan?.term);
			const multiplier =
				loanProduct?.term === "daily"
					? 1
					: loanProduct?.term === "weekly"
						? 7
						: 30;
			const schedule = await Promise.all(
				Array.from({ length: repaymentMonthsCount }).map(async (_, index) => {
					// Calculate due date
					const firstRepaymentDate = loan?.firstRepaymentDate
						? new Date(loan.firstRepaymentDate)
						: new Date();
					const dueDate = new Date(firstRepaymentDate);
					dueDate.setDate(dueDate.getDate() + index * multiplier);
					dueDate.toISOString();

					// Calculate amount and principal per period
					const principal = loan?.appliedAmount
						? Number(loan.appliedAmount) / repaymentMonthsCount
						: 0;
					const roundedPrincipal = Math.round(principal * 100) / 100;

					// Calculate interest per period (simple interest for now)
					let interest = 0;
					interest =
						loanProduct?.term === "annually"
							? loan?.interestRate && loan?.appliedAmount
								? Math.round(
										(Number(loan.interestRate) / 100) *
											Number(loan.appliedAmount) *
											100,
									) / 100
								: 0
							: loanProduct?.term === "monthly"
								? loan?.interestRate && loan?.appliedAmount
									? Math.round(
											(Number(loan.interestRate) / 100) *
												Number(loan.appliedAmount) *
												100,
										) / 100
									: 0
								: loanProduct?.term === "weekly"
									? loan?.interestRate && loan?.appliedAmount
										? Math.round(
												(((Number(loan.interestRate) / 100) * 12) / 365) *
													Number(loan.appliedAmount) *
													7 *
													100,
											) / 100
										: 0
									: loan?.interestRate && loan?.appliedAmount
										? Math.round(
												(((Number(loan.interestRate) / 100) * 12) / 365) *
													Number(loan.appliedAmount) *
													100,
											) / 100
										: 0;

					const roundedInterest = Math.round(interest * 100) / 100;

					// Penalty is fixed
					const penalty = loan?.penalty
						? (Number(loan.penalty) * (principal + interest)) / 100
						: 0;
					const roundedPenalty = Math.round(penalty * 100) / 100;

					// Balance is remaining principal (total - paid so far)
					const balance = loan?.appliedAmount
						? Number(loan.appliedAmount) - principal * (index + 1)
						: 0;
					const roundedBalance = Math.round(balance * 100) / 100;

					// Check if this current date has been paid and is in the loan repayment records
					const repayment = await retrieveLoanRepaymentService(
						TOKEN,
						id,
						dueDate.toISOString(),
					);

					// If paid, adjust status to "paid" else "unpaid"
					const status = repayment?.status === "success" ? "paid" : "unpaid";

					// If past due date and unpaid, apply penalty
					const now = new Date();
					const amount =
						status === "unpaid" && new Date(dueDate) < now
							? principal + interest + penalty
							: principal + interest;
					const roundedAmount = Math.round(amount * 100) / 100;

					return {
						dueDate: dueDate.toISOString().split("T")[0],
						amount: roundedAmount,
						penalty: roundedPenalty,
						principal: roundedPrincipal,
						interest: roundedInterest,
						balance: roundedBalance,
						status: status,
					};
				}),
			);
			setRepaymentSchedule(schedule);
			setSelectedLoanProduct(loanProduct);
		};
		generateRepaymentSchedule();
	}, [TOKEN, loan, id]);

	const { data: loanSummary } = useQuery({
		queryKey: ["loansummary", TOKEN, id],
		queryFn: async () => {
			const customer = await retrieveCustomerService(
				TOKEN,
				loan?.obligor ?? "",
			);
			const transformedResponse = [
				{ name: "Loan ID", value: loan?.serialNo.toString().padStart(10, "0") },
				{
					name: "Borrower",
					value: `${customer.firstName} ${customer.lastName}`,
				},
				{ name: "Account", value: loan?.disbursementAccount },
				{ name: "Status", value: loan?.status },
				{
					name: "First Repayment Date",
					value: loan?.firstRepaymentDate
						? new Date(loan.firstRepaymentDate).toISOString().split("T")[0]
						: "",
				},
				{
					name: "Disbursement Date",
					value: loan?.disbursementDate
						? new Date(loan.disbursementDate).toISOString().split("T")[0]
						: "",
				},
				{ name: "Maturity Date", value: "" },
				{
					name: "Disbursed Amount",
					value: formatAmountDisplay(loan?.appliedAmount ?? 0),
				},
				{ name: "Total Payable", value: "" },
				{ name: "Total Paid", value: "" },
				{ name: "Due Amount", value: "" },
				{ name: "Late Repayment Penalty", value: `${loan?.penalty}%` },
				{ name: "Description", value: loan?.description },
			];
			return transformedResponse;
		},
		enabled: !!TOKEN && !!id && !!loan,
	});

	const convertUrlToFile = async (attachments: string[]): Promise<File[]> => {
		const files: File[] = [];
		for (const url of attachments) {
			try {
				const response = await fetch(url);
				const blob = await response.blob();
				const name = url.split("/collateral_attachment/")[1];
				const file = new File([blob], name, { type: blob.type });
				files.push(file);
			} catch (err) {
				console.error("Failed to fetch attachment:", url, err);
			}
		}
		return files;
	};

	const { data: collaterals, refetch: refetchAllCollateral } = useQuery({
		queryKey: ["collaterals", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllCollateralService(TOKEN, id);
			const transformedResponse = await Promise.all(
				response.map(async (item: any) => {
					const files = await convertUrlToFile(item.attachments);
					return {
						...item,
						attachments: files,
					};
				}),
			);
			return transformedResponse;
		},
		enabled: !!TOKEN,
	});

	const { data: guarantors, refetch: refetchAllGuarantor } = useQuery({
		queryKey: ["guarantors", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllGuarantorService(TOKEN, id);
			return response;
		},
		enabled: !!TOKEN,
	});

	const { data: loanCharges } = useQuery({
		queryKey: ["loancharges", TOKEN, loan?.loanId],
		queryFn: async () => {
			const response = await retrieveAllSystemChargeService(TOKEN);
			const loanCharges = response?.data?.filter(
				(charge: Record<string, any>) =>
					charge?.linkedProductType === "loan" &&
					charge?.linkedProductId === loan?.loanProduct,
			);
			return loanCharges;
		},
		enabled: !!TOKEN && !!loan,
	});

	const handleStepClick = (
		e: React.MouseEvent<HTMLDivElement, MouseEvent>,
		index: number,
	) => {
		e.stopPropagation();
		if (index === activeTabIndex) return;
		setActiveTabIndex(index);
		setSelectedCollateral(null);
		setSelectedGuarantor(null);
		setCollateral(initialCollateral);
		setGuarantor(initialGuarantor);
		return setSelectedCallToAction(null);
	};

	const handleAddCollateralButtonClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.stopPropagation();
		return setSelectedCallToAction("add collateral");
	};

	const handleAddGuarantorButtonClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.stopPropagation();
		return setSelectedCallToAction("add guarantor");
	};

	const handleCollateralTableEllipsisButtonClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		collateral: Record<string, any> | null,
	) => {
		e.stopPropagation();
		e.preventDefault();
		return setSelectedCollateral(collateral);
	};

	const handleGuarantorTableEllipsisButtonClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		guarantor: Record<string, any> | null,
	) => {
		e.stopPropagation();
		e.preventDefault();
		return setSelectedGuarantor(guarantor);
	};

	const handleCollateralTableActionItemClick = async (
		e: React.MouseEvent<HTMLLIElement, MouseEvent>,
		id: string | null,
		action: string,
	) => {
		e.stopPropagation();
		e.preventDefault();
		if (!id) return;
		switch (action) {
			case "Edit":
				setSelectedCallToAction("edit collateral");
				break;
			case "Delete":
				setError(null);
				setIsConfirmationModalOpen(true);
				break;
			default:
				break;
		}
		return;
	};

	const handleGuarantorTableActionItemClick = async (
		e: React.MouseEvent<HTMLLIElement, MouseEvent>,
		id: string | null,
		action: string,
	) => {
		e.stopPropagation();
		e.preventDefault();
		if (!id) return;
		switch (action) {
			case "Edit":
				setSelectedCallToAction("edit guarantor");
				break;
			case "Delete":
				setError(null);
				setIsConfirmationModalOpen(true);
				break;
			default:
				break;
		}
		return;
	};

	const handleCollateralTableDropDownClickOutside = useCallback(
		(event: MouseEvent) => {
			if (
				collateralTableDropdownRef.current &&
				!collateralTableDropdownRef.current.contains(event.target as Node) &&
				isConfirmationModalOpen === false
			) {
				setSelectedCollateral(null);
			}
		},
		[collateralTableDropdownRef, isConfirmationModalOpen],
	);

	const handleGuarantorTableDropDownClickOutside = useCallback(
		(event: MouseEvent) => {
			if (
				guarantorTableDropdownRef.current &&
				!guarantorTableDropdownRef.current.contains(event.target as Node) &&
				isConfirmationModalOpen === false
			) {
				setSelectedGuarantor(null);
			}
		},
		[guarantorTableDropdownRef, isConfirmationModalOpen],
	);

	const handleDeleteCollateral = async (
		collateralId: string,
		institutionId: string,
	) => {
		try {
			const response = await deleteCollateralService(
				TOKEN,
				id,
				institutionId,
				collateralId,
			);
			if (response.status === "success") {
				setIsLoading(false);
				setSelectedCollateral(null);
				setIsConfirmationModalOpen(false);
				refetchAllCollateral();
			} else {
				setIsLoading(false);
				setError("Collateral deletion failed. Please try again.");
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Collateral deletion failed. ${error.message}`);
			console.error("Collateral application deletion failed:", error);
		}
	};

	const handleDeleteGuarantor = async (
		guarantorId: string,
		institutionId: string,
	) => {
		try {
			const response = await deleteGuarantorService(
				TOKEN,
				id,
				institutionId,
				guarantorId,
			);
			if (response.status === "success") {
				setIsLoading(false);
				setSelectedGuarantor(null);
				setIsConfirmationModalOpen(false);
				refetchAllGuarantor();
			} else {
				setIsLoading(false);
				setError("Guarantor deletion failed. Please try again.");
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Guarantor deletion failed. ${error.message}`);
			console.error("Guarantor application deletion failed:", error);
		}
	};

	const handleCollateralTableActionConfirmationModalCallToActionClick = async (
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		actionButton: string,
	) => {
		e.preventDefault();
		e.stopPropagation();
		if (!selectedCollateral?.id || !selectedCollateral?.institution) return;
		switch (actionButton) {
			case "callToActionI":
				setError(null);
				setIsLoading(true);
				await handleDeleteCollateral(
					selectedCollateral?.id,
					selectedCollateral?.institution,
				);
				break;
			case "callToActionII":
				setIsLoading(false);
				setError(null);
				setSelectedCollateral(null);
				setIsConfirmationModalOpen(false);
				break;
			default:
				break;
		}
	};

	const handleGuarantorTableActionConfirmationModalCallToActionClick = async (
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		actionButton: string,
	) => {
		e.preventDefault();
		e.stopPropagation();
		if (!selectedGuarantor?.id || !selectedGuarantor?.institution) return;
		switch (actionButton) {
			case "callToActionI":
				setError(null);
				setIsLoading(true);
				await handleDeleteGuarantor(
					selectedGuarantor?.id,
					selectedGuarantor?.institution,
				);
				break;
			case "callToActionII":
				setIsLoading(false);
				setError(null);
				setSelectedGuarantor(null);
				setIsConfirmationModalOpen(false);
				break;
			default:
				break;
		}
	};

	const handleConfirmationModalPersist = () => {
		return setIsConfirmationModalOpen(true);
	};

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		setIsAlertModalOpen(false);
		setSelectedCollateral(null);
		setSelectedGuarantor(null);
		setCollateral(initialCollateral);
		setGuarantor(initialGuarantor);
		setSelectedCallToAction(null);
		if (activeTabIndex === 6) {
			return navigate("/loanmanagement/application");
		}
		return;
	};

	useEffect(() => {
		if (selectedCollateral !== null) {
			document.addEventListener(
				"mousedown",
				handleCollateralTableDropDownClickOutside,
			);
		} else {
			document.removeEventListener(
				"mousedown",
				handleCollateralTableDropDownClickOutside,
			);
		}
		return () => {
			document.removeEventListener(
				"mousedown",
				handleCollateralTableDropDownClickOutside,
			);
		};
	}, [selectedCollateral, handleCollateralTableDropDownClickOutside]);

	useEffect(() => {
		if (selectedGuarantor !== null) {
			document.addEventListener(
				"mousedown",
				handleGuarantorTableDropDownClickOutside,
			);
		} else {
			document.removeEventListener(
				"mousedown",
				handleGuarantorTableDropDownClickOutside,
			);
		}
		return () => {
			document.removeEventListener(
				"mousedown",
				handleGuarantorTableDropDownClickOutside,
			);
		};
	}, [selectedGuarantor, handleGuarantorTableDropDownClickOutside]);

	const confirmationModalBody = error ? (
		<Box>
			<Typography
				fontFamily={"Inter"}
				fontWeight={"600"}
				fontSize={14}
				lineHeight={"normal"}
				color={"var(--text-salmon-pink-color)"}
				whiteSpace={"normal"}
				marginBlockStart={"calc(var(--basic-padding) / 1.3)"}
			>
				{error}
			</Typography>
		</Box>
	) : null;

	const handleChargeUpdate = (
		e: React.ChangeEvent<HTMLInputElement>,
		charge: string,
	) => {
		e.stopPropagation();
		if (!loan) return;
		setLoan((prevLoan) => {
			if (!prevLoan) return prevLoan;
			const charges = Array.isArray(prevLoan.charges)
				? [...prevLoan.charges]
				: [];
			const chargeIndex = charges.indexOf(charge);
			if (chargeIndex > -1) {
				charges.splice(chargeIndex, 1);
			} else {
				charges.push(charge);
			}
			return { ...prevLoan, charges };
		});
	};

	const handleAddCollateral = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		const formData = new FormData();
		const data = {
			name: collateral.name,
			type: collateral.type,
			serialNo: collateral.serialNo,
			estimatedPrice: collateral.estimatedPrice,
			description: collateral.description,
		};
		Object.entries(data).forEach(([key, value]) => {
			formData.append(key, value);
		});
		collateral.attachments.forEach((file, index) => {
			formData.append("collateralAttachment", file);
		});
		try {
			const response = await addCollateralService(TOKEN, id, formData);
			if (response.status === "success") {
				setIsLoading(false);
				setIsAlertModalOpen(true);
				refetchAllCollateral();
			} else {
				setIsLoading(false);
				setError(
					"Failed to add collateral. Please check credentials and try again.",
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Failed to add collateral.${error.message} `);
			console.error("Failed to add collateral:", error);
		}
	};

	const handleAddGuarantor = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		const formData = new FormData();
		const data = {
			firstName: guarantor.firstName,
			middleName: guarantor.middleName,
			lastName: guarantor.lastName,
			email: guarantor.email,
			primaryPhone: guarantor.primaryPhone,
			secondaryPhone: guarantor.secondaryPhone,
			homeAddress: guarantor.homeAddress,
			officeAddress: guarantor.officeAddress,
			position: guarantor.position,
		};
		Object.entries(data).forEach(([key, value]) => {
			formData.append(key, value);
		});
		try {
			const response = await addGuarantorService(TOKEN, id, formData);
			if (response.status === "success") {
				setIsLoading(false);
				setIsAlertModalOpen(true);
				refetchAllGuarantor();
			} else {
				setIsLoading(false);
				setError(
					"Failed to add guarantor. Please check credentials and try again.",
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Failed to add guarantor.${error.message} `);
			console.error("Failed to add guarantor:", error);
		}
	};

	const handleEditCollateral = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!selectedCollateral?.id || !selectedCollateral?.institution) return;
		setError(null);
		setIsLoading(true);
		const formData = new FormData();
		const data = {
			name: collateral.name,
			type: collateral.type,
			serialNo: collateral.serialNo,
			estimatedPrice: collateral.estimatedPrice,
			description: collateral.description,
		};
		Object.entries(data).forEach(([key, value]) => {
			formData.append(key, value);
		});
		collateral.attachments.forEach((file, index) => {
			if (file.size > 0 && file.type !== "text/html") {
				formData.append("collateralAttachment", file);
			}
		});
		try {
			const response = await editCollateralService(
				TOKEN,
				selectedCollateral?.institution,
				id,
				selectedCollateral?.id,
				formData,
			);
			if (response.status === "success") {
				setIsLoading(false);
				setIsAlertModalOpen(true);
				refetchAllCollateral();
			} else {
				setIsLoading(false);
				setError(
					"Failed to edit collateral. Please check credentials and try again.",
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Failed to edit collateral.${error.message} `);
			console.error("Failed to edit collateral:", error);
		}
	};

	const handleEditGuarantor = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!selectedGuarantor?.id || !selectedGuarantor?.institution) return;
		setError(null);
		setIsLoading(true);
		const formData = new FormData();
		const data = {
			firstName: guarantor.firstName,
			middleName: guarantor.middleName,
			lastName: guarantor.lastName,
			email: guarantor.email,
			primaryPhone: guarantor.primaryPhone,
			secondaryPhone: guarantor.secondaryPhone,
			homeAddress: guarantor.homeAddress,
			officeAddress: guarantor.officeAddress,
			position: guarantor.position,
		};
		Object.entries(data).forEach(([key, value]) => {
			formData.append(key, value);
		});
		try {
			const response = await editGuarantorService(
				TOKEN,
				selectedGuarantor?.institution,
				id,
				selectedGuarantor?.id,
				formData,
			);
			if (response.status === "success") {
				setIsLoading(false);
				setIsAlertModalOpen(true);
				refetchAllGuarantor();
			} else {
				setIsLoading(false);
				setError(
					"Failed to edit guarantor. Please check credentials and try again.",
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Failed to edit guarantor.${error.message} `);
			console.error("Failed to edit guarantor:", error);
		}
	};

	const handleUpdateLoanApplication = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		if (!loan?.institution) return;
		const formData = new FormData();
		const data = {
			obligor: loan?.obligor,
			disbursementAccount: loan?.disbursementAccount,
			loanProduct: loan?.loanProduct,
			loanId: loan?.loanId,
			term: loan?.term,
			interestRate: loan?.interestRate,
			disbursementDate: loan?.disbursementDate,
			firstRepaymentDate: loan?.firstRepaymentDate,
			currency: loan?.currency,
			appliedAmount: loan?.appliedAmount,
			penalty: loan?.penalty,
			description: loan?.description,
		};
		Object.entries(data).forEach(([key, value]) => {
			formData.append(key, value as string | File);
		});
		if (Array.isArray(loan?.charges)) {
			loan.charges.forEach((charge) => {
				formData.append("charges[]", charge);
			});
		}
		loan?.attachments.forEach((file, index) => {
			if (file.size > 0 && file.type !== "text/html") {
				formData.append("loanApplicationAttachment", file);
			}
		});
		try {
			const response = await updateLoanApplicationService(
				TOKEN,
				loan?.institution,
				id,
				formData,
			);
			if (response.status === "success") {
				setIsLoading(false);
				setIsAlertModalOpen(true);
			} else {
				setIsLoading(false);
				setError(
					"Failed to update loan application draft. Please check credentials and try again.",
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Failed to update loan application draft. ${error.message}`);
			console.error("Failed to update loan application draft:", error);
		}
	};

	const handleBookLoan = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!id.trim()) return setBookingError("Loan Id not Found");
		setBookingError(null);
		setIsBookingLoading(true);
		try {
			const response = await bookLoanService(TOKEN, id);
			if (response.status === "success") {
				setIsBookingLoading(false);
				setIsAlertModalOpen(true);
			} else {
				setIsBookingLoading(false);
				setBookingError(
					"Failed to book loan. Please check credentials and try again.",
				);
			}
		} catch (error: any) {
			setIsBookingLoading(false);
			setBookingError(`Failed to book loan. ${error.message}`);
			console.error("Failed to book:", error);
		}
	};

	return (
		<AppLayout pageId={"Book-Loan"} pageTitle={"Book Loan"}>
			<BookLoanWrapper>
				{/* confirmation modal below */}
				<BaseConfirmationModal
					open={isConfirmationModalOpen}
					title="Confirm Action"
					message={`Are you sure you want to delete this ${
						activeTabIndex === 1 ? "collateral" : "Guarantor"
					}?`}
					callToActionI="Confirm"
					callToActionII="Cancel"
					isLoadingI={isLoading}
					body={confirmationModalBody}
					handleClose={handleConfirmationModalPersist}
					handleCallToAction={
						activeTabIndex === 1
							? handleCollateralTableActionConfirmationModalCallToActionClick
							: handleGuarantorTableActionConfirmationModalCallToActionClick
					}
				/>
				{/* confirmation modal above */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalCheckIcon />}
					title={
						activeTabIndex === 1
							? "Collateral Saved Successfully"
							: activeTabIndex === 2
								? "Guarantor Saved Successfully"
								: activeTabIndex === 3
									? "Charges Saved Successfully"
									: "Loan Booked Successfully"
					}
					message={`Your information has been successfully ${
						activeTabIndex === 3 ? "updated" : "added to the system"
					}.`}
					callToAction={`Back to ${
						activeTabIndex === 1
							? "Collateral List"
							: activeTabIndex === 2
								? "Guarantor List"
								: activeTabIndex === 3
									? "Charges"
									: "Drafts"
					}`}
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Stack className="tab-button-stack">
					{steps.map((step, index) => {
						return (
							<Box
								key={index}
								bgcolor={
									activeTabIndex === index
										? "var(--primary-color)"
										: "var(--inactive-tab-color)"
								}
								component={"div"}
								className="tab-button"
								onClick={(e) => handleStepClick(e, index)}
							>
								<Typography
									variant="h3"
									fontFamily={"Poppins"}
									fontWeight={700}
									fontSize={13}
									lineHeight={"normal"}
									color={
										activeTabIndex === index
											? "var(--light-color)"
											: "var(--primary-color)"
									}
								>
									{step}
								</Typography>
							</Box>
						);
					})}
				</Stack>
				{activeTabIndex === 0 && (
					<Box component={"div"} className="loan-summary-table-box">
						<LoanSummaryTable rows={loanSummary as Record<any, any>[]} />
					</Box>
				)}
				{activeTabIndex === 1 && (
					<Fragment>
						{selectedCallToAction === null && (
							<Stack>
								<Box>
									<BaseButton
										variant="contained"
										bgcolor="var(--primary-color)"
										padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
										startIcon={<AddIcon />}
										sx={{
											width: {
												mobile: "-webkit-fill-available",
												miniTablet: "auto",
											},
											float: { miniTablet: "right" },
										}}
										onClick={handleAddCollateralButtonClick}
									>
										<Typography
											variant="button"
											fontFamily="inherit"
											fontWeight="inherit"
											fontSize="inherit"
											lineHeight="inherit"
											color="inherit"
											textTransform="inherit"
										>
											Add Collateral
										</Typography>
									</BaseButton>
								</Box>
								<Box component="div" className="collateral-table-box">
									<CollateralTable
										rows={collaterals ?? []}
										dropdownRef={collateralTableDropdownRef}
										selectedCollateral={selectedCollateral}
										handleActionItemClick={handleCollateralTableActionItemClick}
										handleEllipsisButtonClick={
											handleCollateralTableEllipsisButtonClick
										}
									/>
								</Box>
							</Stack>
						)}
						{selectedCallToAction === "add collateral" && (
							<AddCollateral
								error={error}
								isLoading={isLoading}
								handleSubmit={handleAddCollateral}
								formDetails={collateral}
								setFormDetails={setCollateral}
							/>
						)}
						{selectedCallToAction === "edit collateral" && (
							<EditCollateral
								error={error}
								isLoading={isLoading}
								handleSubmit={handleEditCollateral}
								formDetails={collateral}
								setFormDetails={setCollateral}
								loanId={id}
								collateralId={selectedCollateral?.id}
							/>
						)}
					</Fragment>
				)}
				{activeTabIndex === 2 && (
					<Fragment>
						{selectedCallToAction === null && (
							<Stack>
								<Box>
									<BaseButton
										variant="contained"
										bgcolor="var(--primary-color)"
										padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
										startIcon={<AddIcon />}
										sx={{
											width: {
												mobile: "-webkit-fill-available",
												miniTablet: "auto",
											},
											float: { miniTablet: "right" },
										}}
										onClick={handleAddGuarantorButtonClick}
									>
										<Typography
											variant="button"
											fontFamily="inherit"
											fontWeight="inherit"
											fontSize="inherit"
											lineHeight="inherit"
											color="inherit"
											textTransform="inherit"
										>
											Add Guarantor
										</Typography>
									</BaseButton>
								</Box>
								<Box component="div" className="guarantor-table-box">
									<GuarantorTable
										rows={guarantors ?? []}
										dropdownRef={guarantorTableDropdownRef}
										selectedGuarantor={selectedGuarantor}
										handleActionItemClick={handleGuarantorTableActionItemClick}
										handleEllipsisButtonClick={
											handleGuarantorTableEllipsisButtonClick
										}
									/>
								</Box>
							</Stack>
						)}
						{selectedCallToAction === "add guarantor" && (
							<AddGuarantor
								error={error}
								isLoading={isLoading}
								handleSubmit={handleAddGuarantor}
								formDetails={guarantor}
								setFormDetails={setGuarantor}
							/>
						)}
						{selectedCallToAction === "edit guarantor" && (
							<EditGuarantor
								error={error}
								isLoading={isLoading}
								handleSubmit={handleEditGuarantor}
								formDetails={guarantor}
								setFormDetails={setGuarantor}
								loanId={id}
								guarantorId={selectedGuarantor?.id}
							/>
						)}
					</Fragment>
				)}
				{activeTabIndex === 3 && (
					<Stack gap={"calc(var(--flex-gap))"}>
						<Box component={"div"} className="loan-charges-table-box">
							<LoanChargesTable
								rows={loanCharges as Record<any, any>[]}
								loan={loan}
								handleChargeUpdate={handleChargeUpdate}
							/>
						</Box>
						{error && (
							<Box>
								<Typography
									fontFamily={"Inter"}
									fontWeight={"600"}
									fontSize={14}
									lineHeight={"normal"}
									color={"var(--text-salmon-pink-color)"}
									whiteSpace={"normal"}
								>
									{error}
								</Typography>
							</Box>
						)}
						<Box overflow={"hidden"}>
							<BaseButton
								variant="contained"
								padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
								sx={{
									width: {
										mobile: "-webkit-fill-available",
										miniTablet: "auto",
									},
								}}
								onClick={handleUpdateLoanApplication}
							>
								{isLoading ? (
									<CircularProgress color="inherit" className="loader" />
								) : (
									<Typography
										variant="button"
										fontFamily="inherit"
										fontWeight="inherit"
										fontSize="inherit"
										lineHeight="inherit"
										color="inherit"
										textTransform="inherit"
									>
										Apply
									</Typography>
								)}
							</BaseButton>
						</Box>
					</Stack>
				)}
				{/* If anyone wonders why LoanRepaymentDetailsTable is in Loan Repayment Schedule Box
                and LoanRepaymentSchedule is in Loan Repayment Details Table Box, it's cause Mr. Favour
                asked that I swap places. And this was just easier than renaming components */}
				{activeTabIndex === 4 && (
					<Box component={"div"} className="loan-repayment-schedule-box">
						<LoanRepaymentDetailsTable rows={repaymentSchedule || []} />
					</Box>
				)}
				{activeTabIndex === 5 && (
					<Box component={"div"} className="loan-repayment-details-table-box">
						<LoanRepaymentSchedule rows={repaymentSchedule || []} />
					</Box>
				)}
				{activeTabIndex === 6 && (
					<Box component={"div"}>
						<BookLoanForm
							formDetails={loan}
							setFormDetails={setLoan}
							isLoading={isBookingLoading}
							error={bookingError}
							loanProduct={selectedLoanProduct}
							handleSubmit={handleBookLoan}
						/>
					</Box>
				)}
			</BookLoanWrapper>
		</AppLayout>
	);
};
